// A top-up is a gateway payment (docs/plans/billing-top-up.md): the shapes the API and the portal
// share, snake_case like the rest of the wire. Portal and staff only, never v3 (§0). Money is the
// Money string here and a Dinero inside the API (money.ts); the fee is computed by fee.ts.
import { greaterThan } from "dinero.js";
import { z } from "zod";
import { PageMeta } from "./contact";
import { MONEY_PATTERN, Money, idr, parseMoney } from "./money";

/** The lifecycle's states (T3); the column CHECK and billing/top-up/machine.ts read the same list. */
export const TOP_UP_STATES = [
  "pending",
  "credited",
  "cancelled",
  "expired",
  "disputed",
  "awaiting_second_approval",
  "rejected",
] as const;
export const TopUpState = z.enum(TOP_UP_STATES);
export type TopUpState = z.infer<typeof TopUpState>;

/** The two states finance resolves, with approve or reject (T5). */
export const DISPUTE_STATES = ["disputed", "awaiting_second_approval"] as const satisfies readonly TopUpState[];

/** A key on the wire: 22 Base62 characters, never a raw UUID (screaming.md rule 19). */
const Base62Id = z.string().regex(/^[0-9A-Za-z]{22}$/);

/** A gateway's channel code: "bca_va", "qris". Lower snake case, so it is safe in a log line. */
export const ChannelCode = z.string().regex(/^[a-z][a-z0-9_]{0,31}$/, "invalid_channel");

/** What a channel costs the reseller (T20), on the wire. fee.ts turns it into Dinero. */
export const WireFeeRule = z.discriminatedUnion("kind", [
  z.object({ kind: z.literal("fixed"), amount: Money }),
  z.object({ kind: z.literal("percent"), basis_points: z.int().min(0).max(10_000) }),
]);
export type WireFeeRule = z.infer<typeof WireFeeRule>;

export const TopUpChannel = z.object({ code: ChannelCode, name: z.string(), fee: WireFeeRule });
export type TopUpChannel = z.infer<typeof TopUpChannel>;

/** What the portal offers: the gateway's channels with their fees, and the limits (T9). */
export const TopUpMethods = z.object({
  channels: z.array(TopUpChannel),
  limits: z.object({ min: Money, max: Money }),
});
export type TopUpMethods = z.infer<typeof TopUpMethods>;
export const TopUpMethodsResponse = z.object({ data: TopUpMethods });

// zod runs a refine even when the pattern already failed, so it checks the shape itself.
const positive = (value: string) => MONEY_PATTERN.test(value) && greaterThan(parseMoney(value), idr(0));

/** POST /api/account/top-ups. The limits are the server's (env), so only "more than zero" is here. */
export const RequestTopUpRequest = z.object({
  amount: Money.refine(positive, "must_be_positive"),
  channel: ChannelCode,
});
export type RequestTopUpRequest = z.infer<typeof RequestTopUpRequest>;

export const TopUp = z.object({
  id: Base62Id,
  state: TopUpState,
  /** What the wallet is credited (T4). */
  amount: Money,
  /** The gateway's fee, paid by the reseller (T20). */
  fee: Money,
  /** amount + fee: what the gateway collects. */
  charged: Money,
  gateway: z.string(),
  channel: z.string(),
  /** Where the reseller pays; only while pending, null after. */
  pay_url: z.string().nullable(),
  reject_reason: z.string().nullable(),
  expires_at: z.iso.datetime(),
  created_at: z.iso.datetime(),
  updated_at: z.iso.datetime(),
});
export type TopUp = z.infer<typeof TopUp>;

/** What staff see: whose it is, and who decided it. */
export const StaffTopUp = TopUp.extend({
  reseller_id: Base62Id,
  reseller_name: z.string().nullable(),
  first_approver_id: Base62Id.nullable(),
  decided_by: Base62Id.nullable(),
});
export type StaffTopUp = z.infer<typeof StaffTopUp>;

const page = {
  page: z.coerce.number().int().min(1).default(1),
  per_page: z.coerce.number().int().min(1).max(100).default(25),
};

/**
 * from and to are calendar days in Asia/Jakarta, both inclusive, on when the top-up was made, as
 * the billing history's are (wallet D10). Optional: with none every day shows, so a pending top-up
 * or a dispute from an earlier month is never hidden.
 */
const range = { from: z.iso.date().optional(), to: z.iso.date().optional() };

/** from <= to; ISO dates compare as strings. Exported so a query that extends a base keeps it. */
export const rangeInOrder = <S extends z.ZodType<{ from?: string | undefined; to?: string | undefined }>>(schema: S) =>
  schema.refine((q) => q.from === undefined || q.to === undefined || q.from <= q.to, {
    message: "from_after_to",
    path: ["from"],
  });

/**
 * What the reseller may filter its top-ups by: a state, or in_review, both dispute states at once.
 * The reseller reads the two the same ("finance is looking at it"), so it picks them as one.
 */
export const TOP_UP_LIST_STATES = [...TOP_UP_STATES, "in_review"] as const;
export type TopUpListState = (typeof TOP_UP_LIST_STATES)[number];

export const TopUpListQuery = rangeInOrder(
  z.object({ ...page, ...range, state: z.enum(TOP_UP_LIST_STATES).optional() }),
);
export type TopUpListQuery = z.infer<typeof TopUpListQuery>;

/** The staff queue's fields, unrefined, so the API can swap the reseller for its decoded id. */
export const StaffTopUpListBase = z.object({
  ...page,
  ...range,
  state: TopUpState.optional(),
  reseller: Base62Id.optional(),
});

/** The staff queue: disputes first, then the rest; by state, by reseller and by WIB day. */
export const StaffTopUpListQuery = rangeInOrder(StaffTopUpListBase);
export type StaffTopUpListQuery = z.infer<typeof StaffTopUpListQuery>;

/** reject (staff): the reason is required; the refund happens outside the system (T5). */
export const RejectTopUpRequest = z.object({ reason: z.string().trim().min(1).max(500) });
export type RejectTopUpRequest = z.infer<typeof RejectTopUpRequest>;

export const TopUpResponse = z.object({ data: TopUp });
export const TopUpListResponse = z.object({ data: z.array(TopUp), meta: PageMeta });
export const StaffTopUpResponse = z.object({ data: StaffTopUp });
export const StaffTopUpListResponse = z.object({ data: z.array(StaffTopUp), meta: PageMeta });
